import { API_BASE_URL } from '../../config/api';
import { clearStoredCartAndNotify } from '../../context/cartStorage';
import { clearStoredSubscriptionCartAndNotify } from '../../context/subscriptionCartStorage';
import { stripPhoneDigits, validatePhoneDigitsValue } from '../../utils/phone';
import { SELECTED_ADDRESS_STORAGE_KEY } from '../Cart/cartDistanceUtils';
import { clearStoredCheckoutPayload } from './Payment/paymentCheckoutState';
import { clearStoredPendingOrderPayment } from './Payment/pendingOrderPaymentState';

export const AUTH_STORAGE_KEY = 'digirasa_web_auth';

export const DIGIRASA_AUTH_UPDATED_EVENT = 'digirasa-auth-updated';

/** Query flag: Navbar opens the login modal when present. */
export const LOGIN_QUERY_PARAM = 'login';

/** Query value: path to navigate to after successful login (must be same-origin relative). */
export const AUTH_REDIRECT_QUERY_PARAM = 'redirect';

/** When set, stale local session is cleared and the login modal is shown even if a token exists. */
export const FORCE_REAUTH_QUERY_PARAM = 'reauth';

export const POST_LOGIN_REDIRECT_STORAGE_KEY = 'digirasa_post_login_redirect';

/**
 * @param {string | null | undefined} path
 * @returns {boolean}
 */
export function isSafeRedirectPath(path) {
  if (!path || typeof path !== 'string') return false;
  const p = path.trim();
  if (!p.startsWith('/')) return false;
  if (p.startsWith('//')) return false;
  if (p.includes('://')) return false;
  return true;
}

/**
 * Whether a path should be restored after the next successful login.
 * Excludes destinations that are meaningless post-login (e.g. delete account).
 * @param {string | null | undefined} path
 * @returns {boolean}
 */
export function isUsablePostLoginRedirectPath(path) {
  if (!isSafeRedirectPath(path)) return false;
  const pathname = path.trim().split('?')[0].split('#')[0];
  if (pathname === '/account/delete') return false;
  return true;
}

/**
 * Home URL that triggers the login modal; optional return path after sign-in.
 * @param {string} [returnPath]
 * @param {{ reauth?: boolean }} [options] — `reauth` clears a stale token before showing login.
 */
export function buildAuthRequiredRedirectUrl(returnPath, { reauth = false } = {}) {
  const params = new URLSearchParams();
  params.set(LOGIN_QUERY_PARAM, '1');
  if (isSafeRedirectPath(returnPath)) {
    params.set(AUTH_REDIRECT_QUERY_PARAM, returnPath.trim());
  }
  if (reauth) {
    params.set(FORCE_REAUTH_QUERY_PARAM, '1');
  }
  return `/?${params.toString()}`;
}

/** Default copy when a protected page requires authentication. */
export const SIGN_IN_REQUIRED_MESSAGE = 'Please sign in again.';

/** Remove persisted web auth and notify listeners (Navbar, AccountLayout). */
export function clearStoredAuth() {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  notifyAuthUpdated();
}

/**
 * Clear all user-session data from the browser on sign-out or account deletion.
 * Also clears the browser cart (localStorage). A shared DB cart is planned later
 * so signed-in carts can sync across web and mobile — until then, logout drops
 * the local cart so the next account on this browser does not inherit it.
 * Theme and other non-auth preferences are kept.
 */
export function performWebLogout() {
  clearStoredAuth();
  clearStoredCartAndNotify();
  clearStoredSubscriptionCartAndNotify();
  clearStoredCheckoutPayload();
  clearStoredPendingOrderPayment();
  try {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    // Must match CART_COUPON_STORAGE_KEY in cartCouponStorage.js — avoid importing
    // that module here (it pulls cartCouponUtils → profileFormUtils and cycles).
    sessionStorage.removeItem('digirasa_cart_coupon');
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(SELECTED_ADDRESS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Fired when access token is expired or an authenticated API returns 401. */
export const DIGIRASA_SESSION_EXPIRED_EVENT = 'digirasa-session-expired';

/**
 * On 401, keep the stored user visible and prompt to extend session or log out
 * (see SessionExpiredModal). Does not clear auth immediately.
 * @param {number | undefined | null} status HTTP status
 * @returns {boolean} true when a session-expired prompt was triggered
 */
export function clearStoredAuthIfUnauthorized(status) {
  if (status !== 401) return false;
  if (!readStoredAuth().token) {
    clearStoredAuth();
    return false;
  }
  window.dispatchEvent(
    new CustomEvent(DIGIRASA_SESSION_EXPIRED_EVENT, {
      detail: { reason: 'unauthorized' },
    }),
  );
  return true;
}

/**
 * @param {unknown} err
 * @returns {boolean} true when a session-expired prompt was triggered
 */
export function clearStoredAuthIfApiError(err) {
  if (err && typeof err === 'object' && /** @type {{ status?: number }} */ (err).status === 401) {
    return clearStoredAuthIfUnauthorized(401);
  }
  return false;
}

/**
 * @param {string | null | undefined} message
 * @returns {boolean}
 */
export function isSignInRequiredMessage(message) {
  return /sign in again|not signed in|session expired/i.test(String(message || ''));
}

export async function parseResponseJson(res) {
  const text = await res.text();
  if (!text?.trim()) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

function getValueByPath(obj, path) {
  return path
    .split('.')
    .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

export function getFirstValue(obj, paths) {
  for (const path of paths) {
    const value = getValueByPath(obj, path);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
}

/**
 * Normalize API error bodies (e.g. FastAPI `detail: [{ loc, msg }]`) to a readable string.
 * Avoids passing objects/arrays into `new Error(...)`, which becomes "[object Object]".
 */
export function extractApiErrorMessage(data) {
  if (data == null) return '';
  if (typeof data === 'string') return data.trim();

  const walk = (v) => {
    if (v == null) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (Array.isArray(v)) {
      const parts = v.map(walk).filter((s) => s && String(s).length);
      return parts.join('; ');
    }
    if (typeof v === 'object') {
      if (typeof v.msg === 'string' && v.msg.trim()) return v.msg.trim();
      if (typeof v.message === 'string' && v.message.trim()) return v.message.trim();
      if (v.detail != null) return walk(v.detail);
      if (v.errors != null) return walk(v.errors);
    }
    return '';
  };

  if (typeof data !== 'object') return '';

  for (const k of ['detail', 'message', 'error', 'errors']) {
    if (data[k] != null) {
      const s = walk(data[k]);
      if (s) return s;
    }
  }
  if (data.data && typeof data.data === 'object') {
    for (const k of ['detail', 'message', 'error', 'errors']) {
      if (data.data[k] != null) {
        const s = walk(data.data[k]);
        if (s) return s;
      }
    }
  }
  return '';
}

export function profileFetchErrorMessage(status, data) {
  const fromBody = extractApiErrorMessage(data);
  if (fromBody) return fromBody;
  if (status === 401) return SIGN_IN_REQUIRED_MESSAGE;
  if (status === 404) return 'Profile not found.';
  if (status >= 500) return 'Server error. Try again later.';
  return 'Could not load profile.';
}

export function profileSaveErrorMessage(status, data) {
  const fromBody = extractApiErrorMessage(data);
  if (fromBody) return fromBody;
  if (status === 401) return SIGN_IN_REQUIRED_MESSAGE;
  if (status === 400 || status === 422)
    return 'Invalid profile data. See messages above if any, or try again.';
  if (status === 404) return 'User not found. Please sign in again.';
  if (status === 409) return 'This email is already in use.';
  if (status >= 500) return 'Server error. Try again later.';
  return 'Could not save profile.';
}

/** Map createProfile JSON keys → AccountUpdateDetails state / input `name` */
const CREATE_PROFILE_API_KEY_TO_FIELD = {
  username: 'username',
  email: 'email',
  phone_number: 'phone_number',
  firstName: 'first_name',
  lastName: 'last_name',
  gender: 'gender',
  country: 'country',
  state: 'state',
  city: 'city',
  pincode: 'pincode',
  dateOfBirth: 'date_of_birth',
  date_of_birth: 'date_of_birth',
  guardianName: 'guardian_name',
  guardianEmail: 'guardian_email',
  guardianMobile: 'guardian_mobile',
};

/**
 * Parse FastAPI-style validation errors from createProfile into per-field messages.
 * @returns {{ fieldErrors: Record<string, string>, firstFocusField: string | null }}
 */
export function parseCreateProfileValidationErrors(data) {
  const fieldErrors = {};
  let firstFocusField = null;

  const add = (formKey, msg) => {
    const m = String(msg || '').trim();
    if (!formKey || !m) return;
    if (!fieldErrors[formKey]) fieldErrors[formKey] = m;
    if (firstFocusField == null) firstFocusField = formKey;
  };

  const mapApiSegment = (segment) => {
    if (segment == null) return '_general';
    const key = String(segment);
    return CREATE_PROFILE_API_KEY_TO_FIELD[key] || '_general';
  };

  const consumeItem = (item) => {
    if (item == null) return;
    if (typeof item === 'string') {
      add('_general', item);
      return;
    }
    if (typeof item !== 'object') return;
    const msg = String(item.msg || item.message || '').trim();
    if (!msg) return;
    const loc = Array.isArray(item.loc) ? item.loc : [];
    const bi = loc.indexOf('body');
    const segment = bi >= 0 && loc[bi + 1] != null ? loc[bi + 1] : loc[loc.length - 1];
    const formKey = mapApiSegment(segment);
    add(formKey, msg);
  };

  const consumeDetail = (raw) => {
    if (raw == null) return;
    if (typeof raw === 'string') {
      add('_general', raw);
      return;
    }
    const list = Array.isArray(raw) ? raw : [raw];
    for (const item of list) consumeItem(item);
  };

  if (data && typeof data === 'object') {
    consumeDetail(data.detail);
    if (data.data && typeof data.data === 'object') consumeDetail(data.data.detail);
  }

  const keys = Object.keys(fieldErrors);
  const focus =
    firstFocusField ||
    keys.find((k) => k !== '_general') ||
    (fieldErrors._general ? '_general' : null);

  return { fieldErrors, firstFocusField: focus };
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * HTML date value (YYYY-MM-DD) for createProfile / auth APIs.
 * Backend validates ISO calendar dates; DD-MM-YYYY is rejected as "invalid date format".
 */
export function formatDateOfBirthForCreateProfileApi(iso) {
  const s = String(iso ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
}

export const ageFromIsoDateInput = (iso) => {
  if (!iso?.trim()) return null;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const birth = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const md = today.getMonth() - birth.getMonth();
  if (md < 0 || (md === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age;
};

export const isProfilePersonName = (s) => {
  const t = s.trim();
  if (t.length < 2 || t.length > 50) return false;
  return /^[A-Za-z]+(?:[ \-'][A-Za-z]+)*$/.test(t);
};

/** API date DD-MM-YYYY → HTML date input YYYY-MM-DD */
export function ddmmyyyyToIsoDateInput(value) {
  if (!value || typeof value !== 'string') return '';
  const m = value.trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return '';
  return `${m[3]}-${m[2]}-${m[1]}`;
}

export function readStoredAuth() {
  try {
    const p = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || 'null');
    return { token: p?.token || null, user: p?.user || null, refreshToken: p?.refreshToken || '' };
  } catch {
    return { token: null, user: null, refreshToken: '' };
  }
}

/**
 * Persist auth blob and notify listeners (Navbar, AccountLayout, session watcher).
 * @param {{ token?: string | null, refreshToken?: string, user?: object | null }} patch
 */
export function writeStoredAuth(patch = {}) {
  const current = readStoredAuth();
  const next = {
    token: patch.token !== undefined ? patch.token : current.token,
    refreshToken: patch.refreshToken !== undefined ? patch.refreshToken : current.refreshToken,
    user: patch.user !== undefined ? patch.user : current.user,
  };
  try {
    if (!next.token) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } else {
      localStorage.setItem(
        AUTH_STORAGE_KEY,
        JSON.stringify({
          token: next.token,
          refreshToken: next.refreshToken || '',
          user: next.user || null,
        }),
      );
    }
  } catch {
    /* ignore */
  }
  notifyAuthUpdated();
}

/**
 * Read JWT `exp` (ms since epoch) without verifying signature — for client UX only.
 * @param {string | null | undefined} token
 * @returns {number | null}
 */
export function getAccessTokenExpiryMs(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    const exp = Number(payload?.exp);
    if (!Number.isFinite(exp) || exp <= 0) return null;
    return exp * 1000;
  } catch {
    return null;
  }
}

/** @param {string | null | undefined} token */
export function isAccessTokenExpired(token, skewMs = 0) {
  const expMs = getAccessTokenExpiryMs(token);
  if (expMs == null) return false;
  return Date.now() >= expMs - Number(skewMs || 0);
}

export function notifyAuthUpdated() {
  window.dispatchEvent(new Event(DIGIRASA_AUTH_UPDATED_EVENT));
}

/** Map getProfile (or nested data) to account + form snapshot */
export function profilePayloadToForm(data) {
  const safe = data && typeof data === 'object' ? data : {};
  const root =
    safe.data && typeof safe.data === 'object' && !Array.isArray(safe.data) ? safe.data : safe;

  const first = getFirstValue(root, ['firstName', 'first_name']) || '';
  const last = getFirstValue(root, ['lastName', 'last_name']) || '';
  const dobRaw = getFirstValue(root, ['dateOfBirth', 'date_of_birth']);
  let dateIso = '';
  if (dobRaw) {
    const s = String(dobRaw);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) dateIso = s;
    else dateIso = ddmmyyyyToIsoDateInput(s);
  }

  const phoneRaw = getFirstValue(root, ['phone_number', 'phoneNumber', 'mobile']);
  const phoneDigits = stripPhoneDigits(phoneRaw);

  const idVal = root?.id ?? getFirstValue(safe, ['id', 'data.id']);

  return {
    id: idVal,
    username:
      getFirstValue(root, [
        'username',
        'userName',
        'user_name',
        'loginUsername',
        'user.username',
      ]) || '',
    email: getFirstValue(root, ['email']) || '',
    phone_number: phoneDigits || stripPhoneDigits(phoneRaw),
    form: {
      first_name: first,
      last_name: last,
      gender: getFirstValue(root, ['gender']) || '',
      country: getFirstValue(root, ['country']) || 'India',
      state: getFirstValue(root, ['state']) || '',
      city: getFirstValue(root, ['city']) || '',
      pincode: getFirstValue(root, ['pincode', 'pin_code']) || '',
      date_of_birth: dateIso,
      guardian_name: getFirstValue(root, ['guardianName', 'guardian_name']) || '',
      guardian_email: getFirstValue(root, ['guardianEmail', 'guardian_email']) || '',
      guardian_mobile: getFirstValue(root, ['guardianMobile', 'guardian_mobile']) || '',
    },
  };
}

export async function apiGetProfile(token) {
  const res = await fetch(`${API_BASE_URL}/auth/getProfile`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await parseResponseJson(res);
  return { res, data };
}

export async function apiCreateProfile(token, body) {
  const res = await fetch(`${API_BASE_URL}/auth/createProfile`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const data = await parseResponseJson(res);
  return { res, data };
}

export function isAccountUsername(u) {
  const s = String(u || '').trim();
  if (s.length < 3 || s.length > 50) return false;
  return /^[a-zA-Z0-9_]+$/.test(s);
}

export function validateAccountHeader(account) {
  if (!isAccountUsername(account.username)) {
    return {
      ok: false,
      field: 'username',
      message: 'Username must be 3–50 characters (letters, numbers, and underscores only).',
    };
  }
  if (!EMAIL_RE.test(account.email.trim())) {
    return { ok: false, field: 'email', message: 'Please enter a valid email address.' };
  }
  const phCheck = validatePhoneDigitsValue(account.phone_number);
  if (!phCheck.ok) {
    return {
      ok: false,
      field: 'phone_number',
      message: phCheck.message,
    };
  }
  return { ok: true };
}

export function validateProfileFormFields(profileForm) {
  const fn = profileForm.first_name.trim();
  const ln = profileForm.last_name.trim();
  if (!isProfilePersonName(fn)) {
    return {
      ok: false,
      field: 'first_name',
      message:
        'First name must be 2–50 characters (letters; spaces or hyphens between words if needed).',
    };
  }
  if (!isProfilePersonName(ln)) {
    return {
      ok: false,
      field: 'last_name',
      message:
        'Last name must be 2–50 characters (letters; spaces or hyphens between words if needed).',
    };
  }
  if (!profileForm.gender) {
    return { ok: false, field: 'gender', message: 'Please select gender.' };
  }
  const cityTrim = profileForm.city.trim();
  if (cityTrim.length < 2 || cityTrim.length > 50) {
    return { ok: false, field: 'city', message: 'City must be 2–50 characters.' };
  }
  if (!profileForm.state) {
    return { ok: false, field: 'state', message: 'Please select state.' };
  }
  if (!/^\d{6}$/.test(profileForm.pincode.trim())) {
    return { ok: false, field: 'pincode', message: 'Pincode must be 6 digits.' };
  }
  let age = null;
  if (profileForm.date_of_birth) {
    age = ageFromIsoDateInput(profileForm.date_of_birth);
    if (age == null) {
      return { ok: false, field: 'date_of_birth', message: 'Please enter a valid date of birth.' };
    }
  }

  const gName = profileForm.guardian_name.trim();
  const gEmail = profileForm.guardian_email.trim();
  const guardianRequired = age != null && age < 18;

  if (guardianRequired) {
    if (!isProfilePersonName(gName)) {
      return {
        ok: false,
        field: 'guardian_name',
        message: 'Guardian name is required for users under 18.',
      };
    }
    if (!EMAIL_RE.test(gEmail)) {
      return {
        ok: false,
        field: 'guardian_email',
        message: 'Guardian email is required for users under 18.',
      };
    }
    const gMobRequired = validatePhoneDigitsValue(profileForm.guardian_mobile, { optional: false });
    if (!gMobRequired.ok) {
      return {
        ok: false,
        field: 'guardian_mobile',
        message:
          gMobRequired.message === 'Please enter a valid mobile number.'
            ? 'Guardian mobile is required for users under 18.'
            : gMobRequired.message,
      };
    }
  } else {
    if (gName && !isProfilePersonName(gName)) {
      return {
        ok: false,
        field: 'guardian_name',
        message: 'Guardian name must follow the same rules as your name, or leave blank.',
      };
    }
    if (gEmail && !EMAIL_RE.test(gEmail)) {
      return {
        ok: false,
        field: 'guardian_email',
        message: 'Please enter a valid guardian email, or leave it blank.',
      };
    }
    const gMobCheck = validatePhoneDigitsValue(profileForm.guardian_mobile, { optional: true });
    if (!gMobCheck.ok) {
      return {
        ok: false,
        field: 'guardian_mobile',
        message: gMobCheck.message,
      };
    }
  }
  return { ok: true };
}

export function buildCreateProfileBody(account, profileForm) {
  const fn = profileForm.first_name.trim();
  const ln = profileForm.last_name.trim();
  const cityTrim = profileForm.city.trim();
  const userIdNum = Number(account.id);
  const phone =
    stripPhoneDigits(account.phone_number) || String(account.phone_number || '').replace(/\D/g, '');

  const body = {
    id: userIdNum,
    username: account.username.trim(),
    email: account.email.trim(),
    phone_number: phone,
    firstName: fn,
    lastName: ln,
    gender: profileForm.gender,
    country: (profileForm.country || 'India').trim() || 'India',
    state: profileForm.state,
    city: cityTrim,
    pincode: profileForm.pincode.trim(),
  };

  const dobApi = formatDateOfBirthForCreateProfileApi(profileForm.date_of_birth);
  if (dobApi) body.dateOfBirth = dobApi;

  const gName = profileForm.guardian_name.trim();
  const gEmail = profileForm.guardian_email.trim();
  const gMobDigits = stripPhoneDigits(profileForm.guardian_mobile);
  if (gName) body.guardianName = gName;
  if (gEmail) body.guardianEmail = gEmail;
  if (gMobDigits) body.guardianMobile = gMobDigits;

  return body;
}
